// MIDAS V3 — Marker-line grammar + wire-order block splitter (SP-1)
//
// Part of the v3 segment-streaming FE (V3_SEGMENT_STREAMING_FE_SPEC.md §SP-1 +
// V3_FE_SEGMENT_STREAMING_CONTRACT.md §10.1–§10.2). A v3 (`pipeline_override
// ="v3"`) assistant turn streams short prose "working-update" segments
// interleaved with one-line gray "step-receipt" rows. The bot emits each
// receipt as a MARKER LINE inside the token stream (and again inside the final
// formatted `answer` body): a line matching the pinned grammar below, always
// framed by blank lines. This module turns a message's text into an ORDERED
// block list FROM WIRE ORDER ALONE — no heuristics (§10.2 is normative):
//   • token text            → text block
//   • a marker line          → a settled gray step row AT THAT POSITION
// Marker content is PLAIN TEXT — never markdown-processed, never chip-processed,
// never dangerouslySetInnerHTML'd (the bot neutralizes model-authored
// lookalikes; we still never process it). Copied/exported text keeps the marker
// line verbatim (copy affordances read `message.text`, untouched).
//
// This module is PURE — no React, no Redux, no chip-component imports — so it is
// safe to unit-test in isolation AND to import from the shared chatBot slice
// (mirrors the existing `ccSurface` static import into chatBotSlice.ts).

// ─────────────────────────────────────────────────────────────────────────
//  PINNED MARKER GRAMMAR — CROSS-TEAM CONSTANT (SEC-REQ-3)
// ─────────────────────────────────────────────────────────────────────────
//
//   ^\[(Step|Krok): .{1,120}\]$
//
// This is the SINGLE source of truth on the FE side. It MUST stay byte-for-byte
// in lockstep with the bot's ONE regex constant `_V3_MARKER_GRAMMAR_RE`
// (core/orchestrator.py), which is used both to SYNTHESIZE real markers and to
// NEUTRALIZE model-authored lookalikes. Prefix is `Step` (EN) / `Krok` (SK+CS);
// the label is ≤120 chars and never contains `]` or a newline. ANY change to
// this grammar MUST be made on BOTH sides in the SAME commit and routed through
// `machineid-bot-integration/API_CONTRACTS.md`. Do NOT inline-duplicate this
// regex anywhere — import this constant.
//
// (JS note: the capture group `(Step|Krok)` matches the pinned grammar exactly;
// `.` never matches a newline by default, so `^…$` anchors to a whole single
// line. The line is always tested standalone — never with the `m` flag — so the
// anchors bind to the string boundary of one already-split line.)
export const V3_MARKER_LINE_RE = /^\[(Step|Krok): .{1,120}\]$/;

/** A single block of the ordered wire-order block list. */
export type MarkerBlock =
  | { kind: "text"; text: string }
  | { kind: "marker"; label: string; raw: string };

/**
 * Extract the plain-text label of a marker line already known to match
 * `V3_MARKER_LINE_RE`. The label is the content between the first `": "` (which
 * closes the `[Step`/`[Krok` prefix) and the final `]`. Using the FIRST `": "`
 * is robust to labels that themselves contain `: ` (e.g. "Ratio 2: checked").
 */
const extractMarkerLabel = (markerLine: string): string => {
  const sep = markerLine.indexOf(": ");
  // sep is always >= 0 for a grammar match ("[Step: " / "[Krok: ").
  return markerLine.slice(sep + 2, markerLine.length - 1);
};

/** True when a whole (trimmed) line is a v3 step marker line. */
export const isMarkerLine = (line: string): boolean =>
  V3_MARKER_LINE_RE.test(line.trim());

/**
 * Split a message's text into an ordered block list from wire order alone.
 *
 * A marker line must match the pinned grammar as a WHOLE line. Real markers are
 * blank-line framed (§10.1.2); we CONSUME the framing blank lines so text blocks
 * never carry a stray marker line and never collapse to an empty paragraph
 * ("tolerate leading/trailing surrounding blank lines when splitting"). A marker
 * may be the FIRST block (a turn that opens with a step row — never fabricated).
 *
 * Marker `label` is a plain-text string; `raw` keeps the recognised marker line
 * for reference. No markdown / chip processing is done on marker content here.
 */
export const splitMarkerBlocks = (text: string): MarkerBlock[] => {
  if (!text) return [];
  const lines = text.split("\n");
  const blocks: MarkerBlock[] = [];
  let buf: string[] = [];

  const flushText = () => {
    // Drop leading/trailing blank (whitespace-only) lines so framing blanks
    // around markers don't produce empty text blocks. Internal blank lines
    // (paragraph breaks) are preserved.
    let start = 0;
    let end = buf.length;
    while (start < end && buf[start].trim() === "") start++;
    while (end > start && buf[end - 1].trim() === "") end--;
    if (end > start) {
      blocks.push({ kind: "text", text: buf.slice(start, end).join("\n") });
    }
    buf = [];
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (V3_MARKER_LINE_RE.test(trimmed)) {
      flushText();
      blocks.push({
        kind: "marker",
        label: extractMarkerLabel(trimmed),
        raw: trimmed,
      });
    } else {
      buf.push(line);
    }
  }
  flushText();
  return blocks;
};

/**
 * Count the completed marker lines in a (possibly still-growing) buffer.
 * Backs the SP-2 dedupe rule: when the marker count in the streaming text
 * grows, the inline marker row supersedes the transient loop_progress frame
 * row for that round (marker wins — never render both). Cheap O(lines).
 */
export const countMarkerLines = (text: string): number => {
  if (!text) return 0;
  let n = 0;
  for (const line of text.split("\n")) {
    if (V3_MARKER_LINE_RE.test(line.trim())) n++;
  }
  return n;
};

// ─────────────────────────────────────────────────────────────────────────
//  Streaming safety — hold back a half-streamed marker line
// ─────────────────────────────────────────────────────────────────────────
//
// The jitter buffer releases 3 chars / 30ms, so mid-stream the buffer WILL end
// with a partial marker line (`[`, `[Step: Sea`, …). Rendered as-is it would
// flash `[Step: Sea` as prose before settling into a gray row. This is the
// marker-line twin of `trimUnclosedRefTagTail` (chip-tag hold-back): withhold a
// trailing line that is a strict prefix of a potential marker (no closing `]`
// yet) until it finishes streaming.
//
// The tail regex matches any strict prefix of a marker START — `[`, the partial
// `[Step`/`[Krok` literal, the `: ` separator, and up to 120 label chars — but
// NEVER a `]` (once `]` appears the line is either a complete marker, which
// `splitMarkerBlocks` renders as a row, or a non-marker, which renders as
// prose). A prose line that merely starts with `[` (e.g. a markdown link
// `[text](url)`) diverges within one char (`[t…` fails the `[Step`/`[Krok`
// prefix) and is released immediately. A runaway `[Step: …` with no closing `]`
// stops being held once the label exceeds 120 chars (can't be a valid marker).
const HELD_MARKER_TAIL_RE =
  /^\[(?:S(?:t(?:e(?:p(?::(?: [^\]\n]{0,120})?)?)?)?)?|K(?:r(?:o(?:k(?::(?: [^\]\n]{0,120})?)?)?)?)?)?$/;

/**
 * Split an in-flight buffer into a render-safe prefix + a held tail: hold back a
 * trailing line that is a partial (unclosed) step marker. Returns `{ safe, held }`
 * (mirrors `trimUnclosedRefTagTail`'s shape). The held tail reappears — as a gray
 * row — the moment its closing `]` streams in.
 */
export const holdPartialMarkerTail = (
  buffer: string
): { safe: string; held: string } => {
  if (!buffer) return { safe: "", held: "" };
  // Cheap bail: no `[` anywhere ⇒ no candidate marker to hold.
  if (buffer.indexOf("[") === -1) return { safe: buffer, held: "" };
  const lastNl = buffer.lastIndexOf("\n");
  const tailStart = lastNl + 1; // 0 when the buffer is a single line
  const tail = buffer.slice(tailStart);
  if (HELD_MARKER_TAIL_RE.test(tail)) {
    return { safe: buffer.slice(0, tailStart), held: tail };
  }
  return { safe: buffer, held: "" };
};
